/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { globalRef } from '@amazon/vinyl-util'

/**
 * Session-scoped resume store for music-video playback offsets. Only the
 * last-active video is remembered — saving a new video's offset overwrites the
 * previous one, so returning to an older video starts from 0. Held in memory,
 * never persisted: a page reload starts a VOD fresh (unlike podcast episodes,
 * whose resume round-trips through Firefly and so survives a reload).
 */

interface VideoProgress {
    readonly id: string
    readonly offsetMs: number
}

const progressRef = globalRef<{ stored: VideoProgress | null }>(() => ({ stored: null }))

// One-time cleanup of the dropped persisted key on existing installs.
try {
    localStorage.removeItem('videoPlaybackProgress')
} catch {}

export function saveVideoPlaybackProgress(id: string, progressMs: number): void {
    if (!id || !Number.isFinite(progressMs) || progressMs < 0) return
    progressRef.value.stored = { id, offsetMs: Math.floor(progressMs) }
}

export function readVideoPlaybackProgress(id: string): number {
    const { stored } = progressRef.value
    return id && stored?.id === id ? stored.offsetMs : 0
}

export function clearVideoPlaybackProgress(id: string): void {
    const progress = progressRef.value
    if (id && progress.stored?.id === id) progress.stored = null
}
